import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { gmbApi } from '@/services/gmbApi';
import type { SetupInputsNeeded, SetupInputsAnswers } from '../gmb-manager/types';
import GMBSetupScreen from '../gmb-manager/components/GMBSetupScreen';
import GMBConnectionGate from '../gmb-manager/components/GMBConnectionGate';
import { useGmbConnection } from '../gmb-manager/hooks/useGmbConnection';
import logo from '../../assets/new-logo.png';

const SYNC_POLL_INTERVAL_MS = 10_000;
const SYNC_POLL_MAX_ATTEMPTS = 18; // ~3 minutes; the first sync usually takes ~2

function getUserId() {
  return localStorage.getItem('user_id') || '';
}

/**
 * Shared GMB onboarding route for profile connection and mandatory setup.
 */
export default function GMBOnboardingPage() {
  const navigate = useNavigate();
  const {
    isChecking,
    showGate,
    showSetupScreen,
    profileSetup,
    isOAuthLoading,
    error,
    handleOAuthLogin,
    handleSkip,
  } = useGmbConnection();
  const [isLoadingNeeded, setIsLoadingNeeded] = useState(false);
  const [needed, setNeeded] = useState<SetupInputsNeeded | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const loadNeeded = useCallback(async () => {
    const userId = getUserId();
    if (!userId) {
      setSetupError('User ID not found. Please sign in again.');
      return;
    }
    setIsLoadingNeeded(true);
    setSetupError(null);
    try {
      const response = await gmbApi.getSetupInputsNeeded(userId) as any;
      setNeeded(response?.data || response);
    } catch (err: any) {
      setSetupError(err.message || 'Failed to load GMB setup requirements.');
    } finally {
      setIsLoadingNeeded(false);
    }
  }, []);

  useEffect(() => {
    if (isChecking) return;
    if (showSetupScreen) {
      if (!needed && !isLoadingNeeded && !setupError) loadNeeded();
      return;
    }
    if (!showGate) navigate('/gmb-manager');
  }, [isChecking, showGate, showSetupScreen, needed, isLoadingNeeded, setupError, loadNeeded, navigate]);

  // Right after OAuth the first Google sync is still running, so the backend
  // can't yet tell whether the category matches or service areas exist.
  // Re-check quietly (no spinner, answers stay put) until the sync lands.
  const syncPending = !!needed?.gbp_sync_pending;
  useEffect(() => {
    if (!syncPending) return;
    const userId = getUserId();
    if (!userId) return;
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      try {
        const response = await gmbApi.getSetupInputsNeeded(userId) as any;
        const next = response?.data || response;
        if (next) setNeeded(next);
        if (!next?.gbp_sync_pending) window.clearInterval(timer);
      } catch {
        // Keep the current questions; the next tick retries.
      }
      if (attempts >= SYNC_POLL_MAX_ATTEMPTS) window.clearInterval(timer);
    }, SYNC_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [syncPending]);

  const handleAutoSave = useCallback(async (partial: SetupInputsAnswers) => {
    const userId = getUserId();
    if (!userId) return;
    try {
      // Partial save as the user fills in fields — resumability. Never sets
      // mandatory_setup_completed, so this page keeps re-showing (pre-filled)
      // if the browser closes mid-screen.
      await gmbApi.updateSetupInputs(userId, partial);
    } catch (err: any) {
      toast.error(err.message || 'A setup change could not be saved. Please retry before completing setup.');
    }
  }, []);

  const handleComplete = useCallback(async (answers: SetupInputsAnswers) => {
    const userId = getUserId();
    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      // store_type is BRD-fixed to "single" for V1 — not a user-facing
      // question (DEC-GMB-060/UAT #02), submitted automatically.
      await gmbApi.updateSetupInputs(userId, { ...answers, store_type: 'single' });
      navigate('/gmb-manager');
    } catch (err: any) {
      setSubmissionError(err.message || 'Setup could not be completed. Please retry.');
    } finally {
      setIsSubmitting(false);
    }
  }, [navigate]);

  if (isChecking || (showSetupScreen && (isLoadingNeeded || (!needed && !setupError)))) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">Loading your setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Marketing 4SIGHT Logo" className="h-9 w-auto object-contain flex-shrink-0" />
          <span className="text-gray-300">|</span>
          <span className="text-sm font-medium text-gray-600">GMB Manager Setup</span>
        </div>
        <button
          onClick={() => navigate('/marketing-dashboard')}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer flex items-center gap-1"
        >
          <i className="ri-close-line"></i>
          Exit Setup
        </button>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-10">
          {showGate ? (
            <GMBConnectionGate
              onConnect={handleOAuthLogin}
              onSkip={() => {
                handleSkip();
                navigate('/gmb-manager');
              }}
              isLoading={isOAuthLoading}
              error={error}
              darkMode={false}
              profileSetup={profileSetup}
            />
          ) : setupError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
              <p>{setupError}</p>
              <button
                onClick={loadNeeded}
                className="mt-3 rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          ) : needed ? (
            <>
            {syncPending && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-teal-100 bg-teal-50 px-4 py-3 text-sm text-teal-800">
                <div className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-teal-500 border-t-transparent"></div>
                Fetching your Google Business Profile. Questions that depend on it will update automatically.
              </div>
            )}
            <GMBSetupScreen
              needed={needed}
              darkMode={false}
              onAutoSave={handleAutoSave}
              onComplete={handleComplete}
              isSubmitting={isSubmitting}
              submissionError={submissionError}
            />
            </>
          ) : null}
        </div>

        <footer className="text-center text-xs text-gray-400 py-6">
          <p>&copy; {new Date().getFullYear()} Marketing 4SIGHT. All rights reserved.</p>
        </footer>
      </main>
    </div>
  );
}
